import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  CallOffRefusalDto,
  FrameworkAgreementDto,
  PanelMemberDto,
  PanelStatus,
  UnregisteredPanelMemberDto,
} from "../../autogen/bff-api";

export const activePanel = (framework: Pick<FrameworkAgreementDto, "panel">): PanelMemberDto[] =>
  (framework.panel ?? []).filter((member) => member.status === PanelStatus.Active && !member.contractEnded);

export const reachableUnregistered = (
  framework: Pick<FrameworkAgreementDto, "unregisteredSuppliers">,
): UnregisteredPanelMemberDto[] => (framework.unregisteredSuppliers ?? []).filter((member) => !member.contractEnded);

const termRefusal = (t: TFunction, framework: FrameworkAgreementDto): string | null => {
  switch (framework.callOffRefusal) {
    case CallOffRefusalDto.TermEnded:
      return t("The framework agreement's term has ended, so no more call-offs can be made under it.");
    case CallOffRefusalDto.TermNotStarted:
      return t("The framework agreement's term has not started yet, so no call-offs can be made under it.");
    default:
      return null;
  }
};

export const useTermRefusal = (framework: FrameworkAgreementDto): string | null => {
  const { t } = useTranslation();
  return termRefusal(t, framework);
};

export const remainingUnderCeiling = (framework: Pick<FrameworkAgreementDto, "summary">): number | null =>
  framework.summary.remainingCeilingValue == null ? null : Number(framework.summary.remainingCeilingValue);

export const startRefusal = (
  t: TFunction,
  framework: FrameworkAgreementDto,
  { startsMiniCompetition = false }: { startsMiniCompetition?: boolean } = {},
): string | null => {
  if (!startsMiniCompetition && framework.callOffNotInPlan) return t("NotInPlan");
  const term = termRefusal(t, framework);
  if (term) return term;
  if ((framework.panel ?? []).length === 0 && (framework.unregisteredSuppliers ?? []).length === 0) {
    return t("There are no suppliers on the framework agreement yet. Add one with «{{action}}».", {
      action: t("Add supplier"),
    });
  }
  if (!startsMiniCompetition && activePanel(framework).length === 0 && reachableUnregistered(framework).length > 0) {
    return t(
      "The suppliers on the framework agreement aren't registered in SourceMagnet yet. They can be invited to a mini-competition by e-mail, and can get call-offs once they have registered.",
    );
  }
  if (framework.summary.supplierCount === 0) {
    return (framework.panel ?? []).some((member) => member.contractEnded)
      ? t("No supplier on the framework agreement can get call-offs: each is suspended or its contract has ended.")
      : t("Every supplier on the framework agreement is suspended.");
  }
  if (startsMiniCompetition && framework.summary.supplierCount < 2) return tooFewForAMiniCompetition(t, framework);
  const remaining = remainingUnderCeiling(framework);
  if (remaining === null || remaining > 0) return null;
  return remaining < 0
    ? t("The framework agreement has gone past its ceiling value, so no more call-offs can be made under it.")
    : t("The framework agreement has reached its ceiling value, so no more call-offs can be made under it.");
};

export const useStartRefusal = (
  framework: FrameworkAgreementDto,
  options: { startsMiniCompetition?: boolean } = {},
): string | null => {
  const { t } = useTranslation();
  return startRefusal(t, framework, options);
};

export const tooFewForAMiniCompetition = (
  t: TFunction,
  framework: Pick<FrameworkAgreementDto, "allowsDirectAward" | "panel">,
): string => {
  const panel = framework.panel ?? [];
  const anySuspended = panel.some((member) => member.status === PanelStatus.Suspended);
  const anyContractEnded = panel.some((member) => member.contractEnded);
  if (anyContractEnded && !anySuspended) {
    return framework.allowsDirectAward
      ? t(
          "A mini-competition needs more than one supplier whose contract under the framework agreement is still running. Make a direct call-off instead.",
        )
      : t(
          "A mini-competition needs more than one supplier whose contract under the framework agreement is still running, and this framework agreement allows no direct call-offs, so no call-off can be made under it now.",
        );
  }
  return framework.allowsDirectAward
    ? t("A mini-competition needs more than one active supplier on the panel. Make a direct call-off instead.")
    : t(
        "A mini-competition needs more than one active supplier on the panel, and this framework agreement allows no direct call-offs. Make a suspended supplier active again to run one.",
      );
};

export const byRank = (members: PanelMemberDto[]): PanelMemberDto[] =>
  [...members].sort((a, b) => (a.ranking ?? Number.POSITIVE_INFINITY) - (b.ranking ?? Number.POSITIVE_INFINITY));

export const sharingBestRank = (members: PanelMemberDto[]): PanelMemberDto[] => {
  const ranked = members.filter((member) => member.ranking != null);
  if (ranked.length === 0) return [];
  const best = Math.min(...ranked.map((member) => member.ranking as number));
  const top = ranked.filter((member) => member.ranking === best);
  return top.length > 1 ? top : [];
};

export const defaultCallOffSupplier = (members: PanelMemberDto[]): string =>
  sharingBestRank(members).length > 0 ? "" : (byRank(members)[0]?.organizationId ?? "");
